import React, { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { API_URL, apiFetch } from '../../config/api';
import { buildCommand } from '../../utils/flightCommands';
import { usePushToTalk, speak } from '../../hooks/usePushToTalk';
import { requestVoiceLive, onVoiceLiveState, requestDroneFocus } from '../../utils/voiceLiveBus';

/**
 * DroneCommandBox — the marker popup's command surface: the four actions worth
 * a tap, plus say-or-type for everything else.
 *
 * The popup already knows which drone it belongs to, so the buttons target it
 * directly and a typed line goes out as that drone's selection — "30 meter
 * uthao" on drone A's popup means drone A, no name needed. Parsing stays on
 * /nl/command; execution stays on the same /api routes a button click uses, so
 * authorizeDroneAccess, the connected check and intentService.reserve all still
 * run.
 */

// A popup sits under the cursor while you pan, so a single stray click must
// never arm a drone: the first press arms the button, the second sends it.
const CONFIRM_TIMEOUT_MS = 4000;

const MONO = { fontFamily: 'var(--font-label)', letterSpacing: '0.06em' };

function ActionBtn({ children, onClick, disabled, pending, tone = 'neutral', grow = false }) {
    const palette = {
        neutral: { bg: 'var(--bgTertiary)', fg: 'var(--textPrimary)', bd: 'var(--borderSecondary)' },
        arm: { bg: '#e28a1c', fg: '#1a1c1e', bd: '#e28a1c' },
        danger: { bg: 'transparent', fg: 'var(--danger)', bd: 'var(--danger)' },
    }[tone];

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                ...MONO,
                flex: grow ? 1 : undefined,
                width: grow ? '100%' : 'auto',
                minWidth: 0,
                margin: 0,
                marginBottom: 0,
                height: 30,
                padding: '0 8px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                cursor: disabled ? 'not-allowed' : 'pointer',
                background: pending ? 'var(--warning)' : (disabled ? 'var(--bgTertiary)' : palette.bg),
                color: pending ? '#1a1c1e' : (disabled ? 'var(--textMuted)' : palette.fg),
                border: `1px solid ${pending ? 'var(--warning)' : (disabled ? 'var(--borderSecondary)' : palette.bd)}`,
                boxSizing: 'border-box',
            }}
        >
            {pending ? 'SURE?' : children}
        </button>
    );
}

function DroneCommandBox({ droneId, droneName, telemetry = {} }) {
    const [alt, setAlt] = useState(10);
    const [pending, setPending] = useState(null);
    const [draft, setDraft] = useState('');
    const [status, setStatus] = useState('');
    const [reply, setReply] = useState(null);
    const [intent, setIntent] = useState(null);
    const [busy, setBusy] = useState(false);
    const [liveState, setLiveState] = useState('idle');
    const containerRef = useRef(null);
    const confirmTimer = useRef(null);

    // Leaflet forwards clicks, keystrokes and wheel events on popup content to
    // the map, so typing "3" would zoom and a button press would pan.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        L.DomEvent.disableClickPropagation(el);
        L.DomEvent.disableScrollPropagation(el);
    }, []);

    useEffect(() => onVoiceLiveState(setLiveState), []);
    useEffect(() => () => clearTimeout(confirmTimer.current), []);

    const connected = !!telemetry.connected;
    const armed = !!telemetry.armed;

    const send = useCallback(async (candidate) => {
        const command = buildCommand(candidate);
        if (!command) return;
        setBusy(true);
        setStatus('Sending…');
        try {
            const res = await apiFetch(`${API_URL}${command.endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(command.body),
            });
            const data = await res.json();
            // "Accepted", never "done" — the endpoint returning ok means the
            // command reached the flight controller, not that the aircraft did
            // the thing.
            setReply({
                ok: !!data.success,
                text: data.success
                    ? `Accepted — ${data.message || candidate.action}`
                    : `Rejected — ${data.message || 'no reason given'}`,
            });
        } catch (error) {
            setReply({ ok: false, text: error.message });
        } finally {
            setBusy(false);
            setStatus('');
            setIntent(null);
        }
    }, []);

    const tap = useCallback((action) => {
        clearTimeout(confirmTimer.current);
        if (pending !== action) {
            setPending(action);
            confirmTimer.current = setTimeout(() => setPending(null), CONFIRM_TIMEOUT_MS);
            return;
        }
        setPending(null);
        send({ action, droneId, altitude: alt });
    }, [pending, send, droneId, alt]);

    const parse = useCallback(async (text) => {
        if (!text.trim()) return;
        setDraft('');
        setBusy(true);
        setStatus('Thinking…');
        setIntent(null);
        setReply(null);
        try {
            const res = await apiFetch(`${API_URL}/nl/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text, selectedDroneId: droneId }),
            });
            const data = await res.json();
            if (!data.success) {
                setReply({ ok: false, text: data.message });
                return;
            }
            const parsed = data?.intent;
            if (!parsed) {
                setReply({ ok: false, text: data?.message || 'No intent parsed' });
                return;
            }
            if (parsed.say) {
                setReply({ ok: true, text: parsed.say });
                speak(parsed.say);
            }
            // A camera move needs no confirm and no POST.
            if (parsed.action === 'focus' && parsed.droneId) requestDroneFocus(parsed.droneId);
            if (parsed.needsConfirm) setIntent(parsed);
        } catch (error) {
            setReply({ ok: false, text: error.message });
        } finally {
            setBusy(false);
            setStatus('');
        }
    }, [droneId]);

    const { listening, transcribing, start, stop, mic } = usePushToTalk({
        onText: parse,
        onStatus: setStatus,
        onError: (message) => setReply({ ok: false, text: message }),
    });

    const working = busy || transcribing;
    const blocked = !connected || working;
    const liveOn = liveState === 'live';

    return (
        <div
            ref={containerRef}
            style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px solid var(--borderSecondary)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {/* ── the four actions worth a tap ── */}
            <div style={{ display: 'flex', gap: 6, width: '100%', boxSizing: 'border-box' }}>
                <input
                    type="number"
                    min={1}
                    max={400}
                    value={alt}
                    onChange={(e) => setAlt(Math.max(1, Number(e.target.value) || 1))}
                    aria-label="Takeoff altitude in metres"
                    style={{
                        ...MONO,
                        width: 52,
                        minWidth: 52,
                        maxWidth: 52,
                        height: 30,
                        padding: '0 6px',
                        boxSizing: 'border-box',
                        margin: 0,
                        marginBottom: 0,
                        borderRadius: 6,
                        border: '1px solid var(--borderSecondary)',
                        background: 'var(--inputBg)',
                        color: 'var(--textPrimary)',
                        fontSize: 12,
                    }}
                />
                <ActionBtn onClick={() => tap('takeoff')} disabled={blocked || !armed} pending={pending === 'takeoff'} grow>
                    TAKEOFF
                </ActionBtn>
            </div>

            <div style={{ display: 'flex', gap: 6, width: '100%', boxSizing: 'border-box' }}>
                {armed ? (
                    <ActionBtn onClick={() => tap('disarm')} disabled={blocked} pending={pending === 'disarm'} grow>DISARM</ActionBtn>
                ) : (
                    <ActionBtn onClick={() => tap('arm')} disabled={blocked} pending={pending === 'arm'} tone="arm" grow>ARM</ActionBtn>
                )}
                <ActionBtn onClick={() => tap('land')} disabled={blocked || !armed} pending={pending === 'land'} grow>LAND</ActionBtn>
                <ActionBtn onClick={() => tap('rtl')} disabled={blocked || !armed} pending={pending === 'rtl'} tone="danger" grow>RTL</ActionBtn>
            </div>

            {reply && (
                <div style={{ fontSize: 12, lineHeight: 1.45, color: reply.ok ? 'var(--textSecondary)' : 'var(--danger)', wordBreak: 'break-word' }}>
                    {reply.text}
                </div>
            )}

            {/* A spoken or typed command the backend flagged needsConfirm. */}
            {intent && (
                <ActionBtn onClick={() => send(intent)} disabled={working} tone="arm" grow>
                    {`RUN ${String(intent.action).toUpperCase()}`}
                </ActionBtn>
            )}

            {/* ── say or type anything else ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', boxSizing: 'border-box' }}>
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') parse(draft); }}
                    disabled={working}
                    placeholder={liveOn ? 'Live session on…' : connected ? 'Bolo ya likho…' : 'Not connected'}
                    aria-label={`Command ${droneName || droneId}`}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        width: '100%',
                        height: 30,
                        padding: '0 8px',
                        boxSizing: 'border-box',
                        margin: 0,
                        marginBottom: 0,
                        borderRadius: 6,
                        border: '1px solid var(--borderSecondary)',
                        background: 'var(--inputBg)',
                        color: 'var(--textPrimary)',
                        fontSize: 12,
                        outline: 'none',
                    }}
                />
                <button
                    type="button"
                    onClick={() => parse(draft)}
                    disabled={working || !draft.trim()}
                    title="Send"
                    style={{
                        ...MONO,
                        width: 'auto',
                        minWidth: 'auto',
                        height: 30,
                        padding: '0 12px',
                        margin: 0,
                        marginBottom: 0,
                        flexShrink: 0,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: draft.trim() && !working ? 'pointer' : 'not-allowed',
                        background: draft.trim() && !working ? 'var(--accent)' : 'var(--bgTertiary)',
                        border: 'none',
                        color: draft.trim() && !working ? '#00363d' : 'var(--textMuted)',
                        boxSizing: 'border-box',
                    }}
                >
                    SEND
                </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', boxSizing: 'border-box' }}>
                <button
                    type="button"
                    onMouseDown={start}
                    onMouseUp={stop}
                    onMouseLeave={listening ? stop : undefined}
                    onTouchStart={(e) => { e.preventDefault(); start(); }}
                    onTouchEnd={(e) => { e.preventDefault(); stop(); }}
                    disabled={!mic.ok || working || liveOn}
                    title={mic.ok ? 'Hold the button while you speak' : mic.reason}
                    style={{
                        ...MONO,
                        flex: 1,
                        width: '100%',
                        minWidth: 0,
                        height: 30,
                        margin: 0,
                        marginBottom: 0,
                        padding: '0 6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: mic.ok && !working && !liveOn ? 'pointer' : 'not-allowed',
                        opacity: !mic.ok || liveOn ? 0.4 : 1,
                        background: listening ? 'var(--danger)' : 'var(--bgTertiary)',
                        border: `1px solid ${listening ? 'var(--danger)' : 'var(--borderSecondary)'}`,
                        color: listening ? '#fff' : 'var(--textPrimary)',
                        boxSizing: 'border-box',
                    }}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="2" width="6" height="11" rx="3" />
                        <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                    </svg>
                    <span>{listening ? 'RELEASE' : 'HOLD TO TALK'}</span>
                </button>

                {/* Hands the continuous session to the one voice agent that owns
                    the audio socket, already pointed at the selected drone —
                    a second live session per popup would fight it for the mic. */}
                <button
                    type="button"
                    onClick={requestVoiceLive}
                    title={liveOn ? 'Live session on — click to stop' : 'Start a live voice session on this drone'}
                    style={{
                        ...MONO,
                        flex: 1,
                        width: '100%',
                        minWidth: 0,
                        height: 30,
                        margin: 0,
                        marginBottom: 0,
                        padding: '0 6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: 'pointer',
                        background: liveOn ? 'var(--success)' : 'rgba(0, 218, 243, 0.12)',
                        color: liveOn ? '#04140d' : 'var(--accent)',
                        border: `1px solid ${liveOn ? 'var(--success)' : 'rgba(0, 218, 243, 0.45)'}`,
                        boxSizing: 'border-box',
                    }}
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
                    </svg>
                    <span>{liveState === 'connecting' ? 'CONNECTING' : liveOn ? 'LIVE ON' : 'LIVE'}</span>
                </button>
            </div>

            {(status || !mic.ok) && (
                <div style={{ ...MONO, fontSize: 10, color: 'var(--textMuted)' }}>
                    {status || mic.reason}
                </div>
            )}
        </div>
    );
}

export default DroneCommandBox;
